import React, { useState } from 'react'
import { View, Text, StyleSheet, TouchableOpacity, TextInput } from 'react-native'
import { useNavigation, useTheme } from '@react-navigation/native'
import { BackButtonWeb, Header, Icon } from '@components'
import { Theme } from '@theme'
import { getCommonStyles } from '@utils'
import ContactsList from '../components/ContactsList'
import { useCreateHashtag, useScreen, useKeyboardVisible } from '@hooks'
import CloseButton from '../components/CloseButton'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useLanguage } from '@context'

export const AddHashtag: React.FC = () => {
  const navigation = useNavigation<any>()
  const theme = useTheme()
  const { t } = useLanguage()
  const { isMobile } = useScreen()
  const keyboardVisible = useKeyboardVisible()
  const styles = getStyle(theme)
  const commonStyles = getCommonStyles(theme)
  const [hashtagName, setHashtagName] = useState('')
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>([])
  // While searching contacts on mobile, collapse the name block so the search
  // field and the contacts list stay visible above the keyboard.
  const [isSearchFocused, setIsSearchFocused] = useState(false)
  const { mutate: addHashtag } = useCreateHashtag()

  const handleBack = () => {
    navigation.goBack()
  }

  const goBackToList = () => {
    navigation.navigate('Menu', {
      screen: 'Hashtags',
      params: {
        screen: 'HashtagsList',
      },
    })
  }

  const handleSave = () => {
    if (hashtagName.trim()) {
      // Navigate only once the create succeeds: onSuccess invalidates the
      // 'hashtags' query, so landing on the list after it guarantees the
      // freshly created hashtag is visible. Firing navigation synchronously
      // would race the cache invalidation and hide the new hashtag.
      addHashtag(
        {
          name: '#' + hashtagName.trim().replace('#', ''),
          contactAccountIds: selectedContactIds,
        },
        { onSuccess: () => goBackToList() },
      )
    }
  }

  return (
    <SafeAreaView style={styles.container}>
      {isMobile && (
        <View style={styles.header}>
          <TouchableOpacity onPress={handleBack}>
            <Icon name="arrowBack" size={25} color={theme.colors.black} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>{t('hashtagForm.addTitle')}</Text>
          <View style={styles.placeholder} />
        </View>
      )}

      {!isMobile && <BackButtonWeb title={t('hashtagForm.backToList')} onPress={goBackToList} />}
      <View
        style={[
          styles.content,
          isMobile && commonStyles.paddingTop,
          isMobile && commonStyles.paddingHorizontal,
          !isMobile && commonStyles.searchContainerNonMobileWithoutPaddingTop,
          !isMobile && commonStyles.alignStart,
          !isMobile && commonStyles.gapXxxl,
        ]}
      >
        {!(isMobile && isSearchFocused && keyboardVisible) && (
          <View>
            {!isMobile && <Header title={t('hashtagsScreen.newHashtag')} />}
            {/* Nom du hashtag */}
            <View style={[styles.nameContainer, !isMobile && styles.nameContainerDesktop]}>
              <TextInput
                style={styles.nameInput}
                placeholder={t('hashtagForm.namePlaceholder')}
                value={hashtagName}
                onChangeText={setHashtagName}
                placeholderTextColor={theme.colors.greyBlue}
                autoCapitalize="none"
              />
              <Icon name="edit" size={16} color={theme.colors.greyBlue} />
            </View>
          </View>
        )}
        <ContactsList
          selectedContactIds={selectedContactIds}
          setSelectedContactIds={setSelectedContactIds}
          onSearchFocusChange={setIsSearchFocused}
        />
      </View>

      {/* Bouton Sauvegarder */}
      <View style={[styles.footer, !isMobile && commonStyles.rowBetween]}>
        {!isMobile && <CloseButton onPress={handleBack} />}
        <TouchableOpacity
          style={[styles.saveButton, !hashtagName.trim() && styles.saveButtonDisabled]}
          onPress={handleSave}
          disabled={!hashtagName.trim()}
        >
          <Text style={styles.saveButtonText}>{t('common.save')}</Text>
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  )
}

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.colors.background,
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 16,
      paddingBottom: theme.spacing.md,
      backgroundColor: theme.colors.white,
      borderBottomWidth: 0.5,
      borderBottomColor: theme.colors.lightGrey,
    },
    headerTitle: {
      fontSize: theme.fontSize.large,
      fontWeight: 'bold',
      color: theme.colors.text,
    },
    placeholder: {
      width: 30,
    },
    content: {
      flex: 1,
    },
    nameContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: theme.colors.white,
      borderRadius: 25,
      paddingHorizontal: 16,
      paddingVertical: theme.spacing.sm,
      marginBottom: 16,
      borderWidth: 1,
      borderColor: theme.colors.lightGrey,
    },
    nameContainerDesktop: {
      margin: theme.spacing.lg,
    },
    nameInput: {
      flex: 1,
      fontSize: theme.fontSize.small,
      color: theme.colors.text,
    },
    addContactButton: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: theme.colors.white,
      borderRadius: 12,
      padding: 16,
      marginBottom: 16,
    },
    addContactText: {
      marginLeft: 12,
      flex: 1,
    },
    addContactTitle: {
      fontSize: 16,
      color: theme.colors.text,
      fontWeight: '500',
    },
    addContactSubtitle: {
      fontSize: 13,
      color: theme.colors.greyBlue,
      marginTop: 2,
    },
    selectedContactsContainer: {
      marginBottom: 16,
    },
    selectedContactItem: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: theme.colors.white,
      borderRadius: 12,
      padding: 12,
      marginBottom: 8,
    },
    selectedContactAvatar: {
      width: 32,
      height: 32,
      borderRadius: 16,
      marginRight: 12,
    },
    selectedContactName: {
      flex: 1,
      fontSize: 16,
      color: theme.colors.text,
    },
    removeButton: {
      padding: 4,
    },
    searchContainer: {
      marginBottom: 16,
    },
    searchInputContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: theme.colors.white,
      borderRadius: 20,
      paddingHorizontal: 16,
      paddingVertical: 12,
      shadowColor: theme.colors.black,
      shadowOffset: {
        width: 0,
        height: 1,
      },
      shadowOpacity: 0.1,
      shadowRadius: 2,
      elevation: 2,
    },
    searchIcon: {
      marginRight: 8,
    },
    searchInput: {
      flex: 1,
      fontSize: 16,
      color: theme.colors.text,
    },
    filtersContainer: {
      flexDirection: 'row',
      marginBottom: 16,
    },
    filterButton: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 16,
      borderWidth: 1,
      borderColor: theme.colors.lightGrey,
      marginRight: 8,
      backgroundColor: 'transparent',
    },
    activeFilter: {
      backgroundColor: '#E3F2FD',
      borderColor: theme.colors.primary,
    },
    filterText: {
      fontSize: 14,
      color: theme.colors.greyBlue,
      marginLeft: 4,
    },
    activeFilterText: {
      color: theme.colors.primary,
    },
    hashtagText: {
      fontSize: 16,
      color: theme.colors.greyBlue,
      fontWeight: 'bold',
    },
    contactsListContainer: {
      marginTop: 0,
    },
    footer: {
      padding: 16,
      backgroundColor: theme.colors.white,
    },
    saveButton: {
      backgroundColor: theme.colors.white,
      borderRadius: 25,
      paddingVertical: theme.spacing.sm,
      paddingHorizontal: theme.spacing.xxxl,
      alignItems: 'center',
      borderWidth: 1,
      borderColor: theme.colors.primary,
    },
    saveButtonDisabled: {
      opacity: 0.5,
    },
    saveButtonText: {
      fontSize: theme.fontSize.small,
      fontWeight: '600',
      color: theme.colors.primary,
    },
    modalOverlay: {
      flex: 1,
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
      justifyContent: 'flex-end',
    },
    modalContainer: {
      backgroundColor: theme.colors.white,
      borderTopLeftRadius: 20,
      borderTopRightRadius: 20,
      minHeight: 200,
      maxHeight: '80%',
    },
    handle: {
      width: 36,
      height: 4,
      backgroundColor: theme.colors.lightGrey,
      borderRadius: 2,
      alignSelf: 'center',
      marginTop: 8,
      marginBottom: 8,
    },
    modalHeader: {
      paddingHorizontal: 20,
      paddingVertical: 16,
      borderBottomWidth: 0.5,
      borderBottomColor: theme.colors.lightGrey,
    },
    modalTitle: {
      fontSize: 20,
      fontWeight: '600',
      color: theme.colors.text,
      textAlign: 'center',
    },
    modalContactsList: {
      flex: 1,
    },
    contactItem: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 20,
      paddingVertical: 12,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.lightGrey,
    },
    contactAvatar: {
      width: 40,
      height: 40,
      borderRadius: 20,
      marginRight: 12,
    },
    contactName: {
      flex: 1,
      fontSize: 16,
      color: theme.colors.text,
    },
    checkbox: {
      width: 20,
      height: 20,
      borderRadius: 10,
      borderWidth: 2,
      borderColor: theme.colors.lightGrey,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.colors.white,
    },
    checkboxSelected: {
      backgroundColor: theme.colors.primary,
      borderColor: theme.colors.primary,
    },
    selectionBar: {
      height: 40,
      width: 6,
      borderRadius: 3,
      backgroundColor: 'transparent',
      marginLeft: theme.spacing.md,
    },
    selectionBarActive: {
      backgroundColor: theme.colors.primary,
    },
    overlayTouchable: {
      flex: 1,
      backgroundColor: 'transparent',
    },
    modalFooter: {
      padding: 20,
      borderTopWidth: 0.5,
      borderTopColor: theme.colors.lightGrey,
    },
    modalSaveButton: {
      backgroundColor: theme.colors.primary,
      borderRadius: 25,
      paddingVertical: 16,
      alignItems: 'center',
      justifyContent: 'center',
    },
    modalSaveButtonText: {
      fontSize: 17,
      fontWeight: '600',
      color: theme.colors.white,
    },
    safeArea: {
      flex: 1,
    },
  })

export default AddHashtag
